import React from 'react';
import { motion } from 'framer-motion';
import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import { Search, Bell, User, LayoutGrid, Settings, Database, Activity } from 'lucide-react';
import Logo from './Logo';

const StatusBadge = () => (
    <Flex alignItems="center" gap={1} px={1.5} py={0.5} borderRadius="full" bg="success.100" border="1px solid" borderColor="success.200">
        <Text fontSize="8px" fontWeight="800" color="success.700" whiteSpace="nowrap">SYNCED</Text>
    </Flex>
);

/* Desktop table row — full sync path visible */
const SyncRow = ({ name, source, dest, time, color, initials }) => (
    <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        layout
    >
        <Flex
            alignItems="center"
            py={2.5} px={4}
            borderBottomWidth="1px"
            borderColor="landing.border"
            _hover={{ bg: 'brand.subtle' }}
            transition="background 0.2s"
            gap={3}
        >
            <Box w="28px" h="28px" borderRadius="md" bg={color} display="flex" alignItems="center" justifyContent="center" fontSize="10px" fontWeight="800" flexShrink={0}>
                {initials}
            </Box>
            <Text flex={2} fontSize="xs" fontWeight="700" color="text.main" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" minW={0}>
                {name}
            </Text>
            <Flex flex={2} alignItems="center" gap={1.5}>
                <Text fontSize="9px" fontWeight="600" color="text.secondary">{source} -&gt; {dest}</Text>
            </Flex>
            <Box flexShrink={0}><StatusBadge /></Box>
            <Text flex={1} fontSize="9px" fontWeight="500" color="text.muted" textAlign="right" display={{ base: 'none', md: 'block' }} whiteSpace="nowrap">{time}</Text>
        </Flex>
    </motion.div>
);

/* Mobile sync event card — each card tells the full story */
const SyncCard = ({ name, source, dest, time, color, initials }) => (
    <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        layout
    >
        <Box
            px={3} py={2.5}
            borderBottomWidth="1px"
            borderColor="landing.border"
        >
            {/* Task name + status on one line */}
            <Flex align="center" justify="space-between" mb={1.5}>
                <Flex align="center" gap={1.5} minW={0}>
                    <Box w="20px" h="20px" borderRadius="sm" bg={color} display="flex" alignItems="center" justifyContent="center" fontSize="8px" fontWeight="800" flexShrink={0}>
                        {initials}
                    </Box>
                    <Text fontSize="11px" fontWeight="800" color="text.main" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {name}
                    </Text>
                </Flex>
                <Box flexShrink={0} ml={2}><StatusBadge /></Box>
            </Flex>
            {/* Sync path — always visible in text-only form */}
            <Flex align="center" gap={1.5} pl="28px">
                <Text fontSize="9px" fontWeight="700" color="text.secondary">{source} -&gt; {dest}</Text>
                <Text fontSize="9px" color="text.muted" ml="auto">{time}</Text>
            </Flex>
        </Box>
    </motion.div>
);

const DashboardPreview = () => {
    const rows = [
        { id: 1, name: "Q4 Marketing Epic",  source: "Jira",    dest: "ClickUp", time: "2m ago", initials: "Q4", color: "#DBEAFE" },
        { id: 2, name: "Engineering Sprint", source: "ClickUp", dest: "Jira",    time: "4m ago", initials: "ES", color: "#EDE9FE" },
        { id: 3, name: "Product Roadmap",    source: "Jira",    dest: "ClickUp", time: "7m ago", initials: "PR", color: "#CCFBF1" },
    ];

    const rotationY = useBreakpointValue({ base: 0, lg: -8 }) || 0;
    const rotationX = useBreakpointValue({ base: 0, lg: 6 }) || 0;
    const isMobile = useBreakpointValue({ base: true, lg: false });

    return (
        <Box position="relative" w="100%" maxW="950px" mx="auto" style={!isMobile ? { perspective: '1200px' } : {}}>
            <motion.div
                initial={{ rotateY: 0, rotateX: 0, y: 20, opacity: 0 }}
                animate={{ rotateY: rotationY, rotateX: rotationX, y: 0, opacity: 1 }}
                whileHover={{ rotateY: rotationY / 2, rotateX: rotationX / 2, y: -5 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                style={!isMobile ? { transformStyle: 'preserve-3d' } : {}}
            >
                <Flex
                    bg="landing.card"
                    borderRadius="2xl"
                    boxShadow="0 24px 64px rgba(10, 22, 40, 0.12), 0 0 0 1px rgba(10, 22, 40, 0.05)"
                    overflow="hidden"
                    h={{ base: '340px', md: '420px' }}
                    border="1px solid"
                    borderColor="landing.border"
                >
                    {/* Dark Sidebar — desktop only */}
                    <Flex
                        display={{ base: 'none', md: 'flex' }}
                        direction="column"
                        alignItems="center"
                        w="64px"
                        bg="dark.primary"
                        py={5}
                        gap={6}
                        borderRightWidth="1px"
                        borderColor="rgba(255,255,255,0.05)"
                    >
                        <Box mb={2}>
                            <Logo size={24} color="white" />
                        </Box>
                        
                        <Flex
                            w="40px" h="40px"
                            borderRadius="xl"
                            bg="brand.primary"
                            alignItems="center" justifyContent="center"
                            boxShadow="0 4px 12px rgba(27,79,204,0.4)"
                        >
                            <LayoutGrid size={18} color="white" />
                        </Flex>
                        
                        {[Activity, Database, Settings].map((MenuIcon, i) => (
                            <Flex
                                key={i}
                                w="40px" h="40px"
                                borderRadius="xl"
                                alignItems="center" justifyContent="center"
                                color="whiteAlpha.400"
                                _hover={{ color: "white", bg: "rgba(255,255,255,0.05)" }}
                                transition="all 0.2s"
                                cursor="pointer"
                            >
                                <MenuIcon size={18} />
                            </Flex>
                        ))}
                    </Flex>

                    {/* Main UI Area */}
                    <Flex flex={1} direction="column" bg="landing.surface" minW={0}>
                        
                        {/* Header */}
                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                            px={{ base: 4, md: 6 }} py={{ base: 3, md: 4 }}
                            bg="landing.card"
                            borderBottomWidth="1px"
                            borderColor="landing.border"
                            flexShrink={0}
                        >
                            <Flex alignItems="center" gap={2}>
                                {/* Logo shown only on mobile where sidebar is hidden */}
                                <Box display={{ base: 'flex', md: 'none' }} alignItems="center" mr={1}>
                                    <Logo size={18} />
                                </Box>
                                <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="800" color="text.main" letterSpacing="-0.01em">
                                    Sync Overview
                                </Text>
                            </Flex>
                            <Flex alignItems="center" gap={3}>
                                <Box color="text.muted" display="inline-flex"><Search size={15} /></Box>
                                <Box color="text.muted" display="inline-flex"><Bell size={15} /></Box>
                                <Box w="26px" h="26px" borderRadius="full" bg="brand.subtle" color="brand.primary" display="flex" alignItems="center" justifyContent="center">
                                    <User size={13} strokeWidth={2.5} />
                                </Box>
                            </Flex>
                        </Flex>

                        {/* Metrics Cards */}
                        <Flex gap={3} p={{ base: 3, md: 5 }} flexShrink={0}>
                            <Box flex={1} bg="landing.card" p={{ base: 3, md: 4 }} borderRadius="xl" border="1px solid" borderColor="landing.border" boxShadow="sm" minW={0}>
                                <Text fontSize="9px" fontWeight="700" color="text.muted" letterSpacing="0.05em" mb={1} whiteSpace="nowrap">TASKS SYNCED</Text>
                                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="900" color="text.main">
                                    18,464
                                </Text>
                            </Box>
                            
                            <Box flex={1} bg="landing.card" p={{ base: 3, md: 4 }} borderRadius="xl" border="1px solid" borderColor="landing.border" boxShadow="sm" minW={0}>
                                <Text fontSize="9px" fontWeight="700" color="text.muted" letterSpacing="0.05em" mb={1} whiteSpace="nowrap">ACTIVE CONNECTIONS</Text>
                                <Flex alignItems="center" gap={2}>
                                    <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="900" color="text.main">3</Text>
                                    <Box px={1.5} py={0.5} bg="success.100" color="success.600" borderRadius="md" fontSize="9px" fontWeight="800" whiteSpace="nowrap">100% UP</Box>
                                </Flex>
                            </Box>
                        </Flex>

                        {/* Sync Feed — cards on mobile, table on desktop */}
                        <Box flex={1} bg="landing.card" mx={{ base: 3, md: 5 }} mb={{ base: 3, md: 5 }} borderRadius="xl" border="1px solid" borderColor="landing.border" overflow="hidden" display="flex" flexDirection="column" minH={0}>

                            {/* Mobile: sync cards */}
                            <Box display={{ base: 'block', md: 'none' }}>
                                <Flex px={3} py={2} bg="landing.bg" borderBottom="1px solid" borderColor="landing.border" align="center" justify="space-between">
                                    <Text fontSize="9px" fontWeight="800" color="text.muted" letterSpacing="0.06em">RECENT SYNCS</Text>
                                    <Flex align="center" gap={1}>
                                        <Box w={1.5} h={1.5} borderRadius="full" bg="success.500" style={{ boxShadow: '0 0 4px rgba(52,211,153,0.8)' }} />
                                        <Text fontSize="8px" fontWeight="800" color="success.600">ALL SYNCED</Text>
                                    </Flex>
                                </Flex>
                                {rows.map(row => (
                                    <SyncCard key={row.id} {...row} />
                                ))}
                            </Box>

                            {/* Desktop: full table with sync path column */}
                            <Box display={{ base: 'none', md: 'flex' }} flexDirection="column" flex={1} minH={0}>
                                <Flex px={4} py={2.5} bg="landing.bg" borderBottom="1px solid" borderColor="landing.border" flexShrink={0} gap={3}>
                                    <Text flex={2} fontSize="9px" fontWeight="800" color="text.muted" letterSpacing="0.05em">TASK NAME</Text>
                                    <Text flex={2} fontSize="9px" fontWeight="800" color="text.muted" letterSpacing="0.05em">SYNC PATH</Text>
                                    <Text flex={1} fontSize="9px" fontWeight="800" color="text.muted" letterSpacing="0.05em" textAlign="center">STATUS</Text>
                                    <Text flex={1} fontSize="9px" fontWeight="800" color="text.muted" letterSpacing="0.05em" textAlign="right">LAST SYNC</Text>
                                </Flex>
                                <Box flex={1} overflowY="hidden">
                                    {rows.map(row => (
                                        <SyncRow key={row.id} {...row} />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Flex>
                </Flex>
            </motion.div>
        </Box>
    );
};

export default DashboardPreview;
